import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {z} from "zod/v4-mini"
import {materialFloatOutput, materialNormalOutput, materialPointOutput, materialVectorOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {ExchangeNodeName} from "#material/exchange/node-name"

export interface GeometrySockets extends MaterialNodeSockets {
    inputs: {}
    settings: {
        backfacing: "float" // found in inputs: -; found in settings: float; inferred from data: float
        incoming: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        normal: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        parametric: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        pointiness: "float" // found in inputs: -; found in settings: float; inferred from data: float
        position: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        randomPerIsland: "float" // found in inputs: -; found in settings: float; inferred from data: float
        tangent: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        trueNormal: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
    }
    outputs: {
        position: "point"
        normal: "normal"
        tangent: "normal"
        trueNormal: "normal"
        incoming: "vector"
        parametric: "point"
        backfacing: "float"
        pointiness: "float"
        randomPerIsland: "float"
    }
}

export const geometryDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeNewGeometry,
    },
    nodeClass: "Geometry",
    ui: {
        label: "Geometry",
        color: "#4987af",
        items: [
            {name: "position", valueType: "point", label: "Position", output: true},
            {name: "normal", valueType: "normal", label: "Normal", output: true},
            {name: "tangent", valueType: "normal", label: "Tangent", output: true},
            {name: "trueNormal", valueType: "normal", label: "True Normal", output: true},
            {name: "incoming", valueType: "vector", label: "Incoming", output: true},
            {name: "parametric", valueType: "point", label: "Parametric", output: true},
            {name: "backfacing", valueType: "float", label: "Backfacing", output: true},
            {name: "pointiness", valueType: "float", label: "Pointiness", output: true},
            {name: "randomPerIsland", valueType: "float", label: "Random Per Island", output: true},
        ],
        support: {
            cycles: true,
            three: {full: false, remark: "Parametric, pointiness and random per island have no counterpart."},
        },
    },
    validation: {
        returns: z.strictObject({
            position: materialPointOutput,
            normal: materialNormalOutput,
            tangent: materialNormalOutput,
            trueNormal: materialNormalOutput,
            incoming: materialVectorOutput,
            parametric: materialPointOutput,
            backfacing: materialFloatOutput,
            pointiness: materialFloatOutput,
            randomPerIsland: materialFloatOutput,
        }),
        parameters: z.strictObject({
            inputs: z.strictObject({}),
            settings: z.strictObject({
                backfacing: z.optional(z.number()),
                incoming: z.optional(vec3),
                normal: z.optional(vec3),
                parametric: z.optional(vec3),
                pointiness: z.optional(z.number()),
                position: z.optional(vec3),
                randomPerIsland: z.optional(z.number()), // legacy parameter
                tangent: z.optional(vec3),
                trueNormal: z.optional(vec3),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<GeometrySockets>
